import * as React from 'react';
import { EvidenceOfIncomeDocs, IFinance, IDocumentsList } from '../../../../types';
import ChecklistSection from '../../../atoms/checklist-section';
import Finger, { Status } from '../../../atoms/finger';
import { getVisible, getDocStatus, getLeadSponsorByKey } from '../../../../utilities/documents';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { useIsSectionLocked } from '../../../../hooks/useIsSectionLocked';
import { SectionId } from '../../../molecules/Sidebar/types';

interface IProps {
  id: string;
  infoOnly: boolean;
  financials: IFinance;
  documents: EvidenceOfIncomeDocs;
  title?: string;
  onResume: (url: string) => void;
  onUploadClick: (
    sponsorType: string,
    docType: string | null,
    docKey: string,
    infoOnly: boolean,
    modalTitle: string
  ) => void;
}

const EvidenceOfIncomeSection = (props: IProps) => {
  const { financials, documents: evidenceOfIncome, infoOnly } = props;
  const incomeKey = props.id;
  const offer = useSelector((state: ApplicationState) => state.student.offer);
  const isSectionLocked = useIsSectionLocked(SectionId.FINANCE_DOC, offer.status);
  let hasDocs = false;
  const docNodes: React.ReactNode[] = [];
  const curriculumVitae = evidenceOfIncome.curriculumVitae;
  const employmentReference = evidenceOfIncome.employmentReference;
  const recentEmploymentContract = evidenceOfIncome.recentEmploymentContract;
  const recentPayslip = evidenceOfIncome.recentPayslip;

  const leadSponsor = getLeadSponsorByKey(financials);

  const dispLeadSponsor = leadSponsor === 'Student' ? 'My' : `${leadSponsor}'s`;

  if (curriculumVitae) {
    const visibleDocs = getVisible(curriculumVitae, infoOnly) as IDocumentsList[];

    if (visibleDocs.length > 0) {
      hasDocs = true;
      const modalTitle = `${dispLeadSponsor} CV / Resume`;
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;

          if (item.status === 'REJECTED') {
            return (
              <Finger
                id={`rejected-curriculum-vitae-${incomeKey}-${key}`}
                hideButton={true}
                reason={item.reason}
                key={`rejected-curriculum-vitae-${incomeKey}-${key}`}
                status={Status.Rejected}
                label={`${modalTitle} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
              />
            );
          } else {
            return (
              <Finger
                id={`curriculum-vitae-${incomeKey}-${key}`}
                key={`curriculum-vitae-${incomeKey}-${key}`}
                completeLabel={infoOnly ? 'View' : undefined}
                resolveLabel={infoOnly ? 'Please confirm' : 'Requires attention'}
                buttonLabel={infoOnly ? 'Please review' : 'How to prove'}
                status={getDocStatus(item.status, infoOnly, isSectionLocked)}
                label={`${modalTitle} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
                onClick={() => props.onUploadClick('incomeEvidence', 'curriculumVitae', key, infoOnly, `${modalTitle}`)}
              />
            );
          }
        })
      );
    }
  }

  if (employmentReference) {
    const visibleDocs = getVisible(employmentReference, infoOnly) as IDocumentsList[];
    if (visibleDocs.length > 0) {
      hasDocs = true;
      const modalTitle = `${dispLeadSponsor} Employment Reference`;
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;

          if (item.status === 'REJECTED') {
            return (
              <Finger
                id={`rejected-employment-reference-${incomeKey}-${key}`}
                hideButton={true}
                reason={item.reason}
                key={`rejected-employment-reference-${incomeKey}-${key}`}
                status={Status.Rejected}
                label={`${modalTitle} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
              />
            );
          } else {
            return (
              <Finger
                id={`employment-reference-${incomeKey}-${key}`}
                key={`employment-reference-${incomeKey}-${key}`}
                completeLabel={infoOnly ? 'View' : undefined}
                resolveLabel={infoOnly ? 'Please confirm' : 'Requires attention'}
                buttonLabel={infoOnly ? 'Please review' : 'How to prove'}
                status={getDocStatus(item.status, infoOnly, isSectionLocked)}
                label={`${modalTitle} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
                onClick={() =>
                  props.onUploadClick('incomeEvidence', 'employmentReference', key, infoOnly, `${modalTitle}`)
                }
              />
            );
          }
        })
      );
    }
  }

  if (recentEmploymentContract) {
    const visibleDocs = getVisible(recentEmploymentContract, infoOnly) as IDocumentsList[];
    if (visibleDocs.length > 0) {
      hasDocs = true;
      const modalTitle = `${dispLeadSponsor} Employment Contract`;
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;

          if (item.status === 'REJECTED') {
            return (
              <Finger
                id={`rejected-employment-contract-${incomeKey}-${key}`}
                hideButton={true}
                reason={item.reason}
                key={`rejected-employment-contract-${incomeKey}-${key}`}
                status={Status.Rejected}
                label={`${modalTitle} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
              />
            );
          } else {
            return (
              <Finger
                id={`employment-contract-${incomeKey}-${key}`}
                key={`employment-contract-${incomeKey}-${key}`}
                completeLabel={infoOnly ? 'View' : undefined}
                resolveLabel={infoOnly ? 'Please confirm' : 'Requires attention'}
                buttonLabel={infoOnly ? 'Please review' : 'How to prove'}
                status={getDocStatus(item.status, infoOnly, isSectionLocked)}
                label={`${modalTitle} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
                onClick={() =>
                  props.onUploadClick('incomeEvidence', 'recentEmploymentContract', key, infoOnly, `${modalTitle}`)
                }
              />
            );
          }
        })
      );
    }
  }

  if (recentPayslip) {
    const visibleDocs = getVisible(recentPayslip, infoOnly) as IDocumentsList[];
    if (visibleDocs.length > 0) {
      hasDocs = true;
      const modalTitle = `${dispLeadSponsor} Employment Payslip`;
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;

          if (item.status === 'REJECTED') {
            return (
              <Finger
                id={`rejected-employment-payslip-${incomeKey}-${key}`}
                hideButton={true}
                reason={item.reason}
                key={`rejected-employment-payslip-${incomeKey}-${key}`}
                status={Status.Rejected}
                label={`${modalTitle} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
              />
            );
          } else {
            return (
              <Finger
                id={`employment-payslip-${incomeKey}-${key}`}
                key={`employment-payslip-${incomeKey}-${key}`}
                completeLabel={infoOnly ? 'View' : undefined}
                resolveLabel={infoOnly ? 'Please confirm' : 'Requires attention'}
                buttonLabel={infoOnly ? 'Please review' : 'How to prove'}
                status={getDocStatus(item.status, infoOnly, isSectionLocked)}
                label={`${modalTitle} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
                onClick={() => props.onUploadClick('incomeEvidence', 'recentPayslip', key, infoOnly, `${modalTitle}`)}
              />
            );
          }
        })
      );
    }
  }

  if (!hasDocs) return null;

  return (
    <ChecklistSection
      key={`checklist-section-${incomeKey}`}
      label={leadSponsor === 'Student' ? 'Evidence of Income' : `Evidence of Income from ${leadSponsor}`}
      id={props.id}
    >
      {docNodes}
    </ChecklistSection>
  );
};

export default EvidenceOfIncomeSection;
